import PlusIcon from "@mui/icons-material/Add"
import { Autocomplete, Button, Stack, TextField } from "@mui/material"
import GenericDialog from "@cmp/genericDialog"
import { ProgramDomain } from "@masterschool/course-builder-api"
import { domainToDisplayName } from "@utils/domainUtils"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAppDispatch } from "@app/hooks"
import { createTrack } from "@features/tracks/trackSlice"

export const AddTrackButton = () => {
  const [showPopup, setShowPopup] = useState(false)
  return (
    <>
      {showPopup && <AddTrackPopup onClose={() => setShowPopup(false)} />}
      <Button
        variant="contained"
        startIcon={<PlusIcon />}
        onClick={() => setShowPopup(true)}
        size="small"
      >
        Add track
      </Button>
    </>
  )
}

const AddTrackPopup = (props: { onClose: () => void }) => {
  const { onClose } = props
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [internalName, setInternalName] = useState("")
  const [externalName, setExternalName] = useState("")
  const [domain, setDomain] = useState<ProgramDomain | undefined>(undefined)
  const [durationInMonths, setDurationInMonths] = useState<number | undefined>(
    undefined,
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  // A track needs a name, a school and a real duration to be usable — these
  // were exactly the fields missing when tracks were created by raw insert.
  const isValid =
    internalName.trim().length > 0 &&
    domain !== undefined &&
    durationInMonths !== undefined &&
    durationInMonths >= 1

  const onSubmit = () => {
    if (!isValid || domain === undefined || durationInMonths === undefined) {
      return
    }
    setIsLoading(true)
    setError(undefined)
    dispatch(
      createTrack({
        internalName: internalName.trim(),
        externalName: externalName.trim() || undefined,
        domain,
        durationInMonths,
      }),
    )
      .unwrap()
      .then((track) => navigate(`/track/${track.id}`))
      .catch(() => {
        setIsLoading(false)
        setError("Could not create the track. Please try again.")
      })
  }

  return (
    <GenericDialog
      title="Add track"
      onClose={onClose}
      size="xs"
      content={
        <Stack gap={2}>
          <TextField
            label="Internal name"
            required
            value={internalName}
            onChange={(e) => setInternalName(e.target.value)}
            error={!!error}
          />
          <TextField
            label="Student-facing name (optional)"
            value={externalName}
            onChange={(e) => setExternalName(e.target.value)}
          />
          <Autocomplete
            options={Object.values(ProgramDomain)}
            value={domain ?? null}
            onChange={(_, newValue) =>
              setDomain((newValue as ProgramDomain) ?? undefined)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="School"
                required
              />
            )}
            renderOption={(optionProps, option) => (
              <li {...optionProps} key={option}>
                {domainToDisplayName(option)}
              </li>
            )}
            getOptionLabel={domainToDisplayName}
          />
          <TextField
            label="Duration (months)"
            type="number"
            required
            inputProps={{ min: 1 }}
            value={durationInMonths ?? ""}
            onChange={(e) => {
              const parsed = parseInt(e.target.value, 10)
              setDurationInMonths(Number.isNaN(parsed) ? undefined : parsed)
            }}
            helperText={error}
            error={!!error}
          />
        </Stack>
      }
      buttons={[
        { text: "Cancel", type: "secondary", onClick: onClose },
        {
          text: "Add track",
          type: "primary",
          onClick: onSubmit,
          loading: isLoading,
          disabled: !isValid,
        },
      ]}
      open={true}
    />
  )
}
